<template>
  <div 
    class="group bg-white dark:bg-black rounded-lg overflow-hidden vehicle-card-animation"
    :class="{
      'flex flex-col': props.layout === 'grid' || props.layout === 'compact',
      'flex flex-row gap-4': props.layout === 'list'
    }"
  >
    <!-- Vehicle Image -->
    <div 
      class="relative"
      :class="{
        'w-full aspect-3/2': props.layout === 'grid' || props.layout === 'compact',
        'w-40': props.layout === 'list'
      }"
    >
      <img 
        class="object-cover w-full h-full" 
        :src="props.vehicle.image" 
        :alt="props.vehicle.title"
      >
      <FavoriteButton 
        v-if="props.vehicle.id" 
        :vehicle-id="props.vehicle.id" 
        class="absolute top-2 right-2 bg-white/80 dark:bg-black/80 rounded-full p-1"
      />
    </div>
    
    <!-- Card Content -->
    <div class="flex-1 p-3 flex flex-col h-full">
      <div class="flex items-start justify-between gap-2 mb-2">
        <h2 class="font-medium text-slate-900 dark:text-white text-sm line-clamp-1">
          {{ props.vehicle.title }}
        </h2>
        <button 
          @click="$emit('refresh', props.vehicle.id)"
          class="p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          title="Refresh data"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-slate-500 dark:text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>
      
      <!-- Quick Stats -->
      <div class="flex items-center gap-3 text-xs text-slate-600 dark:text-slate-400">
        <span v-if="props.vehicle.year">{{ props.vehicle.year }}</span>
        <span v-if="props.vehicle.year && props.vehicle.mileage">•</span>
        <span v-if="props.vehicle.mileage">{{ props.vehicle.mileage.toLocaleString() }} km</span>
      </div>
      
      <!-- Price Section -->
      <div class="mt-2">
        <div v-if="props.vehicle.price" class="text-primary font-bold whitespace-nowrap">
          ${{ props.vehicle.price.toLocaleString() }}
        </div>
        <div v-if="props.vehicle.perweek" class="text-xs text-slate-500 dark:text-slate-400">
          From ${{ formatPerWeek(props.vehicle.perweek) }}/week
        </div>
      </div>
      
      <!-- Actions -->
      <div class="flex items-center gap-2 mt-auto pt-3 border-t border-slate-200 dark:border-slate-800">
        <NuxtLink 
          class="flex-1 text-center py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 hover:text-primary dark:hover:text-primary transition-colors"
          :to="`/vehicle-for-sale/${props.vehicle.stockid || props.vehicle.id}/${props.vehicle.slug}`"
        >
          View Details
        </NuxtLink>
        <button
          @click="openQuickView(props.vehicle.id)"
          class="flex-1 text-center py-1.5 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
        >
          Quick View
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useVehicleQuickView } from '~/layers/base/composables/useVehicleQuickView';

interface Vehicle {
  id: string;
  slug: string;
  title: string;
  price?: number;
  perweek?: number;
  year?: string | number;
  mileage?: number;
  image?: string;
}

const props = defineProps<{
  vehicle: Vehicle;
  layout?: 'grid' | 'list' | 'compact';
}>();

const emit = defineEmits<{
  (e: 'refresh', id: string): void;
}>();

const { openQuickView } = useVehicleQuickView();

// Function to format the perweek value
function formatPerWeek(perweek: any): string {
  let value: number;
  if (typeof perweek === 'number') {
    value = perweek;
  } else if (perweek && typeof perweek === 'object' && 'value' in perweek) {
    value = Array.isArray(perweek.value) && perweek.value.length > 0 
      ? Number(perweek.value[0])
      : 0;
  } else {
    value = 0;
  }
  return Math.round(value).toString();
}
</script>

<style scoped>
.vehicle-card-animation {
  opacity: 0;
  transform: translateY(20px);
  animation: fadeInUp 0.5s ease-out forwards;
  transition: all 0.3s ease;
}

.vehicle-card-animation:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style> 